// What a visitor sees while the site is in maintenance, halted, or refusing
// arrivals because it is at capacity.
//
// IT RENDERS INSTEAD OF THE APP, not over it. A modal on top of a working-looking page invites
// people to keep clicking things that will 503, and every one of those clicks produces an
// error toast that contradicts the notice behind it.
//
// THE STATUS ENDPOINT IS THE ONLY THING IT TRUSTS. Not a cached flag, not a 503 from some
// other call: `/api/site-status` is on the middleware's always-open list precisely so this
// component can ask. It polls, so the site coming back up clears the notice on its own — a
// maintenance window that requires every visitor to hard-refresh is a maintenance window that
// keeps costing you traffic after it ends.
//
// ADMINS NEVER SEE IT. The backend lets /api/admin through while everything else is refused,
// so an admin can work during maintenance; showing them this screen would take away the one
// session that needs to verify the fix. The check is `isAdmin`, resolved from the token
// claim, and it fails CLOSED — an unresolved claim shows the notice, because the alternative
// is a visitor seeing a broken app.
import React, { useCallback, useEffect, useState } from 'react';
import { apiService } from '../apiService';

// Slow. The page is static and the only thing that changes is whether we are back; polling
// hard from every open tab is a small self-inflicted load spike aimed at a site that is
// already having a bad day.
const POLL_MS = 30000;

const SiteDownNotice = ({ status }) => (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#F8FAFC]">
        <div className="max-w-md text-center">
            {/* A RETURN PROMISE, NOT A STATE. "Temporarily offline" describes what the site
                IS; every major product describes what happens NEXT — Apple "We'll be back.",
                Twitter "We expect to be back in a couple hours", Flickr "We'll be back with
                the usual photo goodness at about 5am PDT", SoundCloud "soon we'll be up".
                "Offline" is also the specific wrong word: it is what you call a service that
                has been switched off for good, which is the one thing we do not want read
                into a bad afternoon.

                The two headings differ because the two states differ. Maintenance is planned
                and short, so it can promise "shortly"; a halt is unplanned and may not be, so
                it promises a return without promising a time it cannot keep. */}
            {/* THREE STATES, THREE PROMISES. Maintenance is planned and short, so it can
                promise "shortly". A halt is unplanned and may not be, so it promises a return
                without promising a time it cannot keep. AT CAPACITY is neither: nothing is
                broken and nothing needs fixing — there are simply more people here than the
                web tier can serve — so "we'll be back" would be a lie about the state. The
                honest sentence is that it is busy and a few minutes will clear it. */}
            {/* GENERATION DOWN IS NOT A RETURN PROMISE, and that is the point of having it
                as its own state. The other three headings all promise a return, because for
                all three we are the ones who can deliver it. This one is reached for when the
                pipeline is the broken part — a model provider refusing, the render fleet
                unable to start — and "we'll be back shortly" is then a promise about somebody
                else's incident. It names what is unavailable instead, and the line under it
                says the thing people actually want to know: their library is untouched. */}
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
                {status?.status === 'at_capacity' ? "We're a bit busy right now"
                    : status?.status === 'generation_down' ? "Video generation is down"
                    : status?.status === 'maintenance' ? "We'll be back shortly"
                    : "We'll be back"}
            </h1>
            <p className="text-base text-gray-700 leading-relaxed">
                {status?.message
                    || "Sorry — Magi is down right now. We're on it. Please check back in a little while."}
            </p>
            {/* No retry button. It would poll the same endpoint this component already polls,
                and a button that appears to do nothing for thirty seconds reads as broken. */}
            <p className="mt-6 text-[13px] text-gray-600">
                This page checks again automatically.
            </p>

        </div>
    </div>
);

/** Poll site status. Returns `null` while unknown and while the site is serving.
 *
 *  UNKNOWN IS TREATED AS OPEN. If the status call itself fails there is no way to tell a
 *  maintenance window from a flaky connection, and showing "we're down" to someone whose
 *  wifi dropped is worse than letting them try and get a real error. The middleware is the
 *  actual enforcement; this is only how the app explains it.
 */
export const useSiteStatus = (user, enabled = true) => {
    const [status, setStatus] = useState(null);
    // Resolved from the token claim, the same way AdminPage does it. AuthContext does not
    // carry it, and adding it there for one screen would put an async claim read in the
    // boot path of every page.
    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {
        let live = true;
        if (!user?.getIdTokenResult) { setIsAdmin(false); return () => { live = false; }; }
        user.getIdTokenResult()
            .then((r) => { if (live) setIsAdmin(r?.claims?.admin === true); })
            .catch(() => { if (live) setIsAdmin(false); });
        return () => { live = false; };
    }, [user]);

    const check = useCallback(async () => {
        try {
            const next = await apiService.getSiteStatus();
            setStatus(next?.open === false ? next : null);
        } catch {
            setStatus(null);
        }
    }, []);

    useEffect(() => {
        if (!enabled || isAdmin) { setStatus(null); return undefined; }
        check();
        const id = setInterval(check, POLL_MS);
        return () => clearInterval(id);
    }, [enabled, isAdmin, check]);

    return isAdmin ? null : status;
};

export default SiteDownNotice;
